import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import Select from 'react-select';
import { signUpTenant, getIndustries, loginUser } from '../auth/authApi';
import { validateForm } from '../utils/useValidator';
import { isPasswordStrong } from '../utils/passwordUtils';
import { countries } from '../utils/countries';
import { useNavigate, Link } from 'react-router-dom';

const SignupWizard = ({
  email,
  verifiedPhone = '',
  verifiedCountryCode = '',
  onStepChange = () => { },
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [company, setCompany] = useState('');
  const [industry, setIndustry] = useState('');
  const [phone, setPhone] = useState(verifiedPhone);
  const [countryCode, setCountryCode] = useState(() => {
    if (verifiedCountryCode) return verifiedCountryCode;
    const au = countries.find(c => c.country === 'AU');
    return au ? au.code : countries[0].code;
  });
  const hasVerifiedPhone = Boolean(verifiedPhone);
  const [orgAddress, setOrgAddress] = useState('');
  const [orgCity, setOrgCity] = useState('');
  const [orgState, setOrgState] = useState('');
  const [orgPostCode, setOrgPostCode] = useState('');
  const [orgCountry, setOrgCountry] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [orgLogoFile, setOrgLogoFile] = useState(null);
  const maxLogoSize = 2 * 1024 * 1024; // 2MB
  const [industries, setIndustries] = useState([]);
  const [errors, setErrors] = useState({});

  const [dbProgress, setDbProgress] = useState(0);
  const [userProgress, setUserProgress] = useState(0);
  const [privProgress, setPrivProgress] = useState(0);
  const [payload, setPayload] = useState(null);
  const [loginEmail, setLoginEmail] = useState(email);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isAutoSigningIn, setIsAutoSigningIn] = useState(false);
  const autoLoginAttemptedRef = useRef(false);
  const setupPhases = [
    { key: 'db', label: 'Creating database', progress: dbProgress },
    { key: 'user', label: 'Setting up your user account', progress: userProgress },
    { key: 'priv', label: 'Configuring privileges', progress: privProgress },
  ];
  const setupProgress = Math.round(
    (dbProgress + userProgress + privProgress) / setupPhases.length
  );
  const isSetupDone = setupProgress >= 99;

  const passwordChecks = [
    {
      label: 'At least 8 characters',
      passed: password.length >= 8,
    },
    {
      label: 'At least 1 uppercase letter',
      passed: /[A-Z]/.test(password),
    },
    {
      label: 'At least 1 number',
      passed: /[0-9]/.test(password),
    },
    {
      label: 'At least 1 symbol',
      passed: /[^A-Za-z0-9]/.test(password),
    },
  ];
  const isPasswordValid = isPasswordStrong(password);

  const performLogin = async ({ email: loginEmailValue, password: loginPasswordValue }) => {
    setLoginError('');
    try {
      await loginUser({ email: loginEmailValue, password: loginPasswordValue });
      navigate('/setup');
    } catch (err) {
      setLoginError('Invalid email or password');
      throw err;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await performLogin({ email: loginEmail, password: loginPassword });
  };

  useEffect(() => {
    getIndustries().then(setIndustries).catch(() => { });
  }, []);

  useEffect(() => {
    const match = countries.find(c => c.code === countryCode);
    if (match) setOrgCountry(match.label);
  }, [countryCode]);

  useEffect(() => {
    let isCancelled = false;

    if (step === 'setup' && payload) {
      setDbProgress(0);
      setUserProgress(0);
      setPrivProgress(0);

      const run = async () => {
        const signupPromise = signUpTenant(payload).catch(() => { });

        const runProgressBar = async (setter, duration) => {
          const steps = 99;
          for (let i = 1; i <= steps; i++) {
            if (isCancelled) return;
            await new Promise(res => setTimeout(res, duration / steps));
            if (isCancelled) return;
            setter(i);
          }
        };

        const progressPromise = (async () => {
          await runProgressBar(setDbProgress, 18000);
          await runProgressBar(setUserProgress, 5000);
          await runProgressBar(setPrivProgress, 4000);
        })();

        await Promise.all([signupPromise, progressPromise]);

        if (!isCancelled) {
          setDbProgress(100);
          setUserProgress(100);
          setPrivProgress(100);
          setStep('complete');
        }
      };

      run();
    }

    return () => {
      isCancelled = true;
    };
  }, [step, payload]);

  useEffect(() => {
    onStepChange(step);
  }, [step, onStepChange]);

  useEffect(() => {
    if (step !== 'complete' || autoLoginAttemptedRef.current) {
      return undefined;
    }

    autoLoginAttemptedRef.current = true;
    setIsAutoSigningIn(true);

    performLogin({ email, password })
      .catch(() => {
        setLoginEmail(email);
        setLoginPassword(password);
      })
      .finally(() => {
        setIsAutoSigningIn(false);
      });

    return undefined;
  }, [step, email, password]);

  useEffect(() => {
    setErrors((current) => {
      const nextErrors = { ...current };

      if (!confirmPassword) {
        delete nextErrors.confirmPassword;
        return nextErrors;
      }

      if (password !== confirmPassword) {
        nextErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete nextErrors.confirmPassword;
      }

      return nextErrors;
    });
  }, [password, confirmPassword]);

  const handleAboutNext = () => {
    const fields = [
      { field: 'name', label: 'Name', required: true },
      { field: 'password', label: 'Password', required: true },
      { field: 'phone', label: 'Phone Number', required: true },
    ];
    const formData = { name, password, phone };
    const errs = validateForm(fields, formData);
    if (name && !/^[A-Za-z0-9_ ]{6,}$/.test(name)) {
      errs.name = 'Name must be 6+ chars without symbols';
    }
    if (phone && phone.replace(/\D/g, '').length <= 7) {
      errs.phone = 'This is not a Phone number format';
    }
    if (password && !isPasswordStrong(password)) {
      errs.password = 'Password must be 8+ chars';
    }
    if (!confirmPassword) {
      errs.confirmPassword = 'Please re-type your password';
    } else if (password !== confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep('organisation');
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOrgLogoFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setLogoPreview(base64);
      setOrgLogo(base64);
    };
    reader.onerror = () => {
      console.error('File reading failed');
    };
    reader.readAsDataURL(file);
  };

  const handleOrgNext = () => {
    const fields = [
      { field: 'company', label: 'Company Name', required: true },
      { field: 'industry', label: 'Industry', required: true },
      { field: 'orgLogo', label: 'Logo' },
    ];
    const formData = { company, industry, orgLogo: orgLogoFile };
    const errs = validateForm(fields, formData);
    if (company && !/^[A-Za-z0-9_ ]{1,30}$/.test(company)) {
      errs.company = 'Company must be less than 30 characters without symbols';
    }
    if (!industry || isNaN(industry)) {
      errs.industry = 'Industry selection is required';
    }
    if (orgLogoFile && orgLogoFile.size > maxLogoSize) {
      errs.orgLogo = 'Logo must be smaller than 2MB';
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const data = {
      email,
      name,
      password,
      company,
      industryId: industry ? Number(industry) : undefined,
      phone,
      countryCode,
      orgAddress,
      orgCity,
      orgState,
      orgPostCode,
      orgCountry,
      orgLogo
    };

    setPayload(data);
    setStep('setup');
  };

  const renderAboutForm = () => (
    <form>
      <fieldset className="wizard-fieldset show">
        <h6 className="text-lg text-neutral-500 mb-3">
          We&apos;ll use this info to create your secure workspace.</h6>
        <p> It only takes a moment, and we&apos;ll never share your details.</p>
        <div className="mb-3 position-relative">
          <label className="form-label">Email</label>
          <input type="email" className="form-control pe-5" value={email} disabled />
          <span
            className="position-absolute end-0 translate-middle-y d-flex align-items-center gap-1 me-3 text-success"
            style={{ pointerEvents: 'none', top: '48px' }}
          >
            <span>verified</span>
            <Icon icon="mdi:check-circle" />
          </span>
        </div>
        <div className="mb-3">
          <label className="form-label">Your Name</label>
          <input
            type="text"
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            value={name}
            onChange={e => setName(e.target.value)}
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Choose Password</label>
          <div className="position-relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`form-control ${errors.password ? 'is-invalid' : ''} ${password.length > 0 ? 'pe-5' : ''}`}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            {password.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 position-absolute top-50 end-0 translate-middle-y me-3 text-decoration-none"
                style={{ color: '#212529' }}
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon
                  icon={showPassword ? 'solar:eye-closed-linear' : 'solar:eye-outline'}
                  className="text-base"
                  style={{ color: '#212529', fill: '#212529', stroke: '#212529' }}
                />
              </button>
            )}
            {isPasswordValid && (
              <span
                className="position-absolute top-50 end-0 translate-middle-y me-5 text-success d-flex align-items-center"
                style={{ pointerEvents: 'none' }}
              >
                <Icon icon="mdi:check-circle" />
              </span>
            )}
          </div>
          {errors.password && <div className="invalid-feedback">{errors.password}</div>}
          {password.length > 0 && !isPasswordValid && (
            <div className="mt-3 d-flex flex-column gap-2">
              {passwordChecks.map((check) => {
                const isMet = check.passed;

                return (
                  <div
                    key={check.label}
                    className="d-flex align-items-center gap-2"
                    style={{
                      color: isMet ? '#198754' : '#fd7e14',
                    }}
                  >
                    <Icon
                      icon={isMet ? 'mdi:check-circle' : 'mdi:alert-circle'}
                      style={{
                        fontSize: '1.1rem',
                        flexShrink: 0,
                      }}
                    />
                    <span className="text-sm">
                      {check.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">Re-type Password</label>
          <input
            type="password"
            className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
          {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
        </div>
        <div className="mb-3 d-flex gap-2">
          <div>
            <label className="form-label">Country Code</label>
            <select
              className="form-select"
              value={countryCode}
              onChange={e => setCountryCode(e.target.value)}
              disabled={hasVerifiedPhone}
            >
              {countries.map(c => (
                <option key={c.country} value={c.code}>{c.label} ({c.code})</option>
              ))}
            </select>
          </div>
          <div className="flex-grow-1">
            <label className="form-label">Phone Number</label>
            <input
              type="text"
              className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
              value={phone}
              onChange={e => setPhone(e.target.value)}
              disabled={hasVerifiedPhone}
            />
            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
          </div>
        </div>
        <div className="form-group text-end mt-16">
          <button type="button" onClick={handleAboutNext} className="btn btn-primary px-32">
            Next
          </button>
        </div>
      </fieldset>
    </form>
  );

  const renderOrganisationForm = () => (
    <form>
      <fieldset className="wizard-fieldset show">
        <div className="row g-4 align-items-start">
          <div className="col-12 col-lg-8">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Your Company Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  maxLength="30"
                  className={`form-control ${errors.company ? 'is-invalid' : ''}`}
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                />
                {errors.company && <div className="invalid-feedback">{errors.company}</div>}
              </div>
              <div className="col-12">
                <label className="form-label">Industry <span className="text-danger">*</span></label>
                <Select
                  classNamePrefix={`react-select ${errors.industry ? 'is-invalid' : ''}`}
                  value={industries.map(i => ({ value: i.id, label: i.name, icon: i.icon })).find(o => o.value === industry) || null}
                  onChange={opt => setIndustry(opt?.value || '')}
                  options={industries.map(i => ({ value: i.id, label: i.name, icon: i.icon }))}
                  isClearable
                  placeholder="Select industry"
                  formatOptionLabel={(option) => (
                    <div className="d-flex align-items-center gap-1">
                      <Icon icon={option.icon} /> {option.label}
                    </div>
                  )}
                />
                {errors.industry && <div className="react-select-invalid">{errors.industry}</div>}
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className="card h-100 p-0 shadow-none" style={{ marginBottom: 0 }}>
              <div className="card-body p-24 pb-0 d-flex align-items-center justify-content-center">
                <div className="upload-image-wrapper d-flex align-items-center gap-2 flex-wrap justify-content-center">
                  <div className="uploaded-imgs-container d-flex flex-wrap justify-content-center">
                    {logoPreview && (
                      <div className="position-relative h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-100">
                        <button
                          type="button"
                          className="uploaded-img__remove position-absolute top-0 end-0 z-1 text-2xxl line-height-1 me-8 mt-8 d-flex"
                          onClick={() => {
                            setLogoPreview('');
                            setOrgLogo('');
                            setOrgLogoFile(null);
                            setErrors(prev => { const { orgLogo, ...rest } = prev; return rest; });
                          }}
                        >
                          <Icon icon="radix-icons:cross-2" className="text-xl text-danger-600" />
                        </button>
                        <img className="w-100 h-100 object-fit-cover" src={logoPreview} alt="Uploaded Preview" />
                      </div>
                    )}
                  </div>
                  {!logoPreview ? (
                    <label
                      className="upload-file-multiple h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-100 bg-hover-neutral-200 d-flex align-items-center flex-column justify-content-center gap-1"
                      htmlFor="upload-file-multiple"
                    >
                      <Icon icon="solar:camera-outline" className="text-xl text-secondary-light" />
                      <span className="form-label fw-normal text-secondary-light text-center mb-0 px-2">Upload company logo</span>
                      <input id="upload-file-multiple" type="file" hidden onChange={handleLogoChange} />
                    </label>
                  ) : (
                    <label
                      className="btn btn-link p-0 d-inline-flex align-items-center gap-1 text-decoration-none"
                      style={{ color: '#212529' }}
                      htmlFor="upload-file-multiple"
                    >
                      <Icon
                        icon="solar:upload-outline"
                        className="text-base"
                        style={{ color: '#212529', fill: '#212529', stroke: '#212529' }}
                      />
                      <span>Replace</span>
                      <input id="upload-file-multiple" type="file" hidden onChange={handleLogoChange} />
                    </label>
                  )}
                </div>
                {errors.orgLogo && <div className="text-danger mt-2">{errors.orgLogo}</div>}
              </div>
            </div>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Address</label>
            <input
              type="text"
              className="form-control"
              value={orgAddress}
              onChange={e => setOrgAddress(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">City</label>
            <input
              type="text"
              className="form-control"
              value={orgCity}
              onChange={e => setOrgCity(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">State</label>
            <input
              type="text"
              className="form-control"
              value={orgState}
              onChange={e => setOrgState(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">Post Code</label>
            <input
              type="text"
              className="form-control"
              value={orgPostCode}
              onChange={e => setOrgPostCode(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">Country</label>
            <select
              className="form-select"
              value={orgCountry}
              onChange={e => setOrgCountry(e.target.value)}
              disabled
            >
              {countries.map(c => (
                <option key={c.label} value={c.label}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group text-end mt-16">
          <button type="button" onClick={handleOrgNext} className="btn btn-primary px-32">
            Next
          </button>
        </div>
      </fieldset>
    </form>
  );

  const renderSetupForm = () => {
    const currentPhaseIndex = setupProgress < 34
      ? 0
      : setupProgress < 67
        ? 1
        : 2;
    const currentPhase = setupPhases[currentPhaseIndex];
    const currentPhaseLabel = isSetupDone ? 'Done' : currentPhase.label;

    return (
      <div>
        <h6 className="text-lg text-neutral-500 mb-3">We’re setting up your personal system in the background.</h6>
        <p>We&apos;re taking care of the heavy lifting. Sit back and relax while we set things up.</p>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold">{currentPhaseLabel}</span>
            <span className="text-secondary">{setupProgress}%</span>
          </div>
          <div className="progress">
            <div
              className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
              style={{ width: `${setupProgress}%` }}
            />
          </div>
        </div>
        <div className="d-flex flex-column gap-2">
          {setupPhases.map((phase, index) => {
            const isComplete = isSetupDone || setupProgress >= ((index + 1) * 100) / setupPhases.length;
            const isActive = index === currentPhaseIndex && !isComplete;
            const phaseLabel = isSetupDone && index === setupPhases.length - 1 ? 'Done' : phase.label;

            return (
              <div key={phase.key} className="d-flex align-items-center gap-2">
                <Icon
                  icon={isComplete ? 'mdi:check-circle' : isActive ? 'mdi:progress-clock' : 'mdi:circle-outline'}
                  className={isComplete ? 'text-success' : isActive ? 'text-primary' : 'text-secondary'}
                  style={{ fontSize: '1.1rem', flexShrink: 0 }}
                />
                <span className={isComplete ? 'text-success fw-semibold' : isActive ? 'text-primary fw-semibold' : 'text-secondary'}>
                  {phaseLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin}>
      <p>Use your email and password to sign in. </p>
      {isAutoSigningIn && (
        <div className="alert alert-info py-2" role="alert">
          Signing you in...
        </div>
      )}
      <div className="mb-3">
        <label className="form-label">Email</label>
        <input type="email" className="form-control" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
      </div>
      <div className="mb-3">
        <label className="form-label">Password</label>
        <input type="password" className="form-control" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
      </div>
      <div className="mb-3 text-end">
        {loginError && <div className="text-danger mb-2">{loginError}</div>}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <Link to="/forgot-password" className="text-primary-600">Forgot Password?</Link>
          <button type="submit" className="btn btn-primary">Sign In</button>
        </div>
      </div>
    </form>
  );

  return (
    <div className="form-wizard">
      {step === 'form' && renderAboutForm()}
      {step === 'organisation' && renderOrganisationForm()}
      {step === 'setup' && renderSetupForm()}
      {step === 'complete' && renderLoginForm()}
    </div>
  );
};

export default SignupWizard;
